import type { JSX } from 'react';

import { RevealConnect } from './RevealConnect';
import { RevealFound } from './RevealFound';
import type {
  NetworkApplyDecisions,
  NetworkUserConstraints,
} from '../../lib/networkRecommendation';
import type { RevealCandidate } from '../../lib/revealStaging';
import type { SourceAnalysisState } from '../../lib/useSourceAnalysis';

// The merged Gateways stage (reveal-gateways-onetab): ONE panel that presents the
// gateway scan AND the primary-gateway decision as a single progressive beat,
// rather than the old two stages ("Gateways" + "Connections"). Three beats,
// driven by existing signals:
//
//   scanning    (!settled)                        → the live found list (RevealFound)
//   determining (settled && analysis === loading) → "analyzing your network", no
//                                                    forward control (advancing is
//                                                    held until the pick lands, D2)
//   ready       (settled && analysis ready/none)  → the NetworkRecommendation pick
//                                                    + reason + edit, or the escape
//                                                    when analysis can't run
//
// On arrival with an already-settled scan the scanning beat is SKIPPED: the panel
// opens on the completed scan and moves straight to the decision beat (the boat-
// reveal "arriving after the scan completed" scenario). Both beats share the
// stage testid `reveal-stage-found`, so the page/e2e treat them as one stage.
// Forward gating lives in the page (dataStageFor / the footer) and in the
// decision beat itself (no Continue while determining).

export interface RevealGatewaysProps {
  /** Discovered gateways (the scan beat list + the "weighing these" preview). */
  candidates: RevealCandidate[];
  candidateCount: number;
  /** Scan reached its terminus (device_inventory or complete). */
  settled: boolean;
  /** A snapshot has been mirrored at all. */
  hasSnapshot: boolean;
  /** The boat hasn't reported in yet (connecting grace). */
  connecting: boolean;
  /** Current setup phase — drives the search/determining bars (fill + stage text). */
  phase: string | null;
  /** Source-analysis lane state — drives the determining/ready beats. */
  analysis: SourceAnalysisState;
  /** Live device count — surfaced as concurrent background progress. */
  deviceCount: number;
  /** STAGE the confirmed/edited gateway plan (applied later, at Devices→Alarms). */
  onApply: (decisions: NetworkApplyDecisions) => Promise<void>;
  /**
   * Re-run the AI against the user's edits as HARD constraints (Settings "Tune
   * your network" reevaluate flow) — passed through to the pick's CTA.
   */
  onReevaluate?: (constraints: NetworkUserConstraints) => Promise<void>;
  /** True while a submitted reevaluate is in flight — locks the pick controls. */
  reevaluating?: boolean;
  /** Kick a re-analysis from retained candidates. */
  onTriggerAnalysis: () => void;
  /** Can we trigger analysis right now (boat online, session known)? */
  canTriggerAnalysis: boolean;
  /** Advance to the device stage (the pick is staged, or skipped). */
  onContinue: () => void;
}

export function RevealGateways(props: RevealGatewaysProps): JSX.Element {
  // Scanning beat — skipped on arrival when the scan already settled.
  if (!props.settled) {
    return (
      <RevealFound
        candidates={props.candidates}
        candidateCount={props.candidateCount}
        settled={props.settled}
        hasSnapshot={props.hasSnapshot}
        connecting={props.connecting}
        phase={props.phase}
      />
    );
  }
  // Determining / ready beat — the primary-gateway decision, in the same stage.
  return (
    <RevealConnect
      analysis={props.analysis}
      candidates={props.candidates}
      deviceCount={props.deviceCount}
      onApply={props.onApply}
      onReevaluate={props.onReevaluate}
      reevaluating={props.reevaluating}
      onTriggerAnalysis={props.onTriggerAnalysis}
      canTriggerAnalysis={props.canTriggerAnalysis}
      phase={props.phase}
      onContinue={props.onContinue}
    />
  );
}
